<!--
  YOU — the phone's fourth tab, and the destination of the avatar on a desktop.

  ── THIS SCREEN IS NOW MY JOVII AS WELL ──────────────────────────────────────────

  "My Jovii" left the dock in the phone-UX pass (TabBar.svelte) and its content
  came here, which is Netflix's "My Netflix" arrangement and, more to the point,
  the arrangement this app should always have had: the saved shelf, the
  suggestions and the continue row are all answers to "what is mine", and so is
  the profile. `/my` redirects to `/profile` and the route survives for old links.

  So the page is, in order: who you are (a BUTTON — see below), Downloads,
  Continue watching if there is anything in progress, My List, and Suggested.
  `MyJovi.svelte` keeps the fuller history screen behind its own route; what
  moved here is the three things a phone reaches for.

  ── THE HEADER IS A BUTTON, AND EVERYTHING ELSE IS BEHIND IT ────────────────────

  The name and mark used to be an inline editor with a pencil, a handle popup and
  four stacked panels below them — a settings screen wearing a profile's clothes.
  It is one row with a chevron now, and pressing it opens a SHEET holding the big
  avatar, the name field, and four rows: App settings, Account, Help, Sign out.
  Everything that was on the page is still reachable; none of it is in the way of
  the shelf, which is what a person actually opened this tab for.

  ── ONE NAME, ONE OWNER ────────────────────────────────────────────────────────

  Unchanged, and it must stay unchanged. There are three places a display name
  could live: `prefs.name` in localStorage, the name the sync engine proposes at
  `hello`, and `displayName` on the account. WHEN THERE IS AN ACCOUNT, THE ACCOUNT
  OWNS THE NAME. This screen is its only editor, it writes the same string down
  into `prefs.name` so the room server is told the same thing, and on load it
  reconciles once — adopting the server's name, or pushing the local one up if the
  account is still on the minted default.

  ── WHAT THE INVITE GATE DOES AND DOES NOT COVER ───────────────────────────────

  Your handle, display name and mark are findable by ANY account behind the invite
  code, because that is what makes "add me, I'm amber-otter" work. What you are
  watching goes to accepted friends and nowhere else. Both facts are on the
  screen, under App settings, not just in a doc.

  NOTHING ABOUT IDENTITY OR STORAGE KEYS CHANGES HERE. `branding.test.ts` pins the
  page title, the keys and the routes; this pass moves pixels only.
-->
<script lang="ts">
  import { tick } from 'svelte';
  import Button from '$lib/ui/Button.svelte';
  import Downloads from '$lib/ui/Downloads.svelte';
  import Empty from '$lib/ui/Empty.svelte';
  import Icon from '$lib/ui/Icon.svelte';
  import LazyRail from '$lib/ui/LazyRail.svelte';
  import PlaybackPrefs from '$lib/ui/PlaybackPrefs.svelte';
  import AccountDevices from '$lib/ui/AccountDevices.svelte';
  import ContinueCard from '$lib/ui/ContinueCard.svelte';
  import InstallGuide from '$lib/ui/InstallGuide.svelte';
  import Poster from '$lib/ui/Poster.svelte';
  import Rail from '$lib/ui/Rail.svelte';
  import Sheet from '$lib/ui/Sheet.svelte';
  import TileGrid from '$lib/ui/TileGrid.svelte';
  import { gate } from '$lib/ui/session.svelte';
  import Spinner from '$lib/ui/Spinner.svelte';
  import { continueWatching, prefs, progress, recent, saved } from '$lib/ui/prefs.svelte';
  import { continueTiles } from '$lib/ui/history-groups';
  import { taste } from '$lib/ui/shelf';
  import { sizeLabel, groupDownloads, totalBytes } from '$lib/offline/index-state';
  import { bootDownloads, downloads } from '$lib/offline/manager.svelte';
  import AvatarMark from '$lib/social/AvatarMark.svelte';
  import AvatarPicker from '$lib/social/AvatarPicker.svelte';
  import { onDate, until } from '$lib/social/dates';
  import { social } from '$lib/social/store';
  import { normaliseHandle, type AvatarId } from '$lib/social/protocol';
  import { avatars as fetchAvatars } from '$lib/social/api';
  import { untrack } from 'svelte';

  const store = social();
  const view = $derived($store);
  const me = $derived(view.me);

  /* ── loading ─────────────────────────────────────────────────────────── */

  $effect(() => {
    store.dismiss();
    void store.load();
  });

  /** The pickable set, straight from the server so a picker need not hardcode it. Falls
   *  back to the compiled-in list inside `AvatarPicker` if this never answers. */
  let offered = $state<AvatarId[] | null>(null);
  $effect(() => {
    const abort = new AbortController();
    void fetchAvatars(abort.signal)
      .then((b) => (offered = b.avatars))
      .catch(() => undefined);
    return () => abort.abort();
  });

  /* ── the name ────────────────────────────────────────────────────────── */

  /**
   * Reconcile once, when the account first arrives. `guest` is the minted default and
   * means "never set", so a browser that already had a name in Settings keeps it and
   * teaches the account; otherwise the account teaches the browser.
   */
  let reconciled = false;
  $effect(() => {
    const account = me;
    if (account === null || reconciled) return;
    reconciled = true;
    untrack(() => {
      const local = prefs.name.trim();
      if (account.displayName !== 'guest' && account.displayName !== '') {
        if (local !== account.displayName) prefs.name = account.displayName;
        draftName = account.displayName;
      } else if (local !== '') {
        draftName = local;
        void saveName(local);
      } else {
        draftName = '';
      }
    });
  });

  let draftName = $state('');
  const nameDirty = $derived(me !== null && draftName.trim() !== '' && draftName.trim() !== me.displayName);

  async function saveName(next: string): Promise<void> {
    const clean = next.trim().slice(0, 32);
    if (clean === '') return;
    const ok = await store.saveProfile({ displayName: clean });
    if (!ok) return;
    // One string, written down wherever it is read. `prefs.name` is what Watch hands the
    // room server at `join`, and `setDisplayName` is what a socket that is ALREADY open
    // needs to be told, so both, in that order.
    prefs.name = clean;
    const mod = await import('$lib/player/sync-shared');
    mod.setDisplayName(clean);
  }

  /* ── the mark ────────────────────────────────────────────────────────── */

  let picking = $state(false);

  async function pickAvatar(avatar: AvatarId): Promise<void> {
    if (me?.avatar === avatar) {
      picking = false;
      return;
    }
    const ok = await store.saveProfile({ avatar });
    if (ok) picking = false;
  }

  /* ── the handle ──────────────────────────────────────────────────────── */

  let editing = $state(false);
  let draftHandle = $state('');
  let checked = $state<{ handle: string; available: boolean; message: string | null } | null>(null);
  let copied = $state(false);
  let handleInput = $state<HTMLInputElement | null>(null);

  /** 0 means "you may change it now"; anything else is the server's own remainder. */
  let handleDeadline = $state(0);
  let handleNow = $state(Date.now());
  $effect(() => {
    const remaining = me?.handleChangeInMs ?? 0;
    const now = Date.now();
    handleNow = now;
    handleDeadline = now + Math.max(0, remaining);
    if (remaining <= 0) return;
    const timer = setInterval(() => { handleNow = Date.now(); }, 1000);
    return () => clearInterval(timer);
  });
  const lockedFor = $derived(Math.max(0, handleDeadline - handleNow));
  const locked = $derived(lockedFor > 0);

  function openEditor(): void {
    if (gate.accounts) return;
    editing = !editing;
    if (editing) {
      draftHandle = me?.handle ?? '';
      checked = null;
      store.dismiss();
    }
  }

  /* Focus lands in the field when the editor opens — programmatic, not `autofocus`,
     which the a11y checker rightly flags on things that appear mid-page. */
  $effect(() => {
    if (editing && !locked) handleInput?.focus();
  });

  /**
   * Ask the server whether a handle is free, 350 ms after typing stops. The answer is
   * advisory: the change itself is checked again server-side, because a name that was
   * free while you were typing can be taken by the time you press the button.
   */
  $effect(() => {
    const wanted = draftHandle;
    if (gate.accounts || !editing) return;
    const normalised = normaliseHandle(wanted);
    if (normalised === null || normalised === me?.handle) {
      checked = null;
      return;
    }
    const abort = new AbortController();
    const timer = setTimeout(() => {
      void store.checkHandle(normalised, abort.signal).then((result) => {
        if (abort.signal.aborted || result === null) return;
        checked = {
          handle: normalised,
          available: result.available,
          message: result.message
        };
      });
    }, 350);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  });

  async function submitHandle(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (gate.accounts) return;
    const ok = await store.changeHandle(draftHandle);
    if (ok) {
      editing = false;
      checked = null;
    }
  }

  async function copyHandle(): Promise<void> {
    if (me === null) return;
    const text = `@${me.handle}`;
    try {
      await navigator.clipboard.writeText(text);
      copied = true;
      setTimeout(() => (copied = false), 1600);
    } catch {
      // Clipboard is gated on a secure context and on permission, and iOS refuses it
      // outside a real gesture. Selecting the text is the fallback that always works.
      const el = document.getElementById('jt-handle');
      if (el !== null && typeof window.getSelection === 'function') {
        const range = document.createRange();
        range.selectNodeContents(el);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  }

  const busyProfile = $derived(view.busy.includes('profile'));
  const busyHandle = $derived(view.busy.includes('handle'));

  /* ── the shelf that moved here from My Jovii ───────────────────────────── */

  /* ONE TILE PER SERIES — same rule and same function as Home's row, so the two
     screens cannot drift. `continueWatching(Infinity)` is the eligibility test
     (started, not finished, past the 30 s noise floor); the grouping then
     collapses a show's episodes into its latest resume point, and the limit is
     applied to TILES, not to episodes, so a binge cannot hide the rest. */
  const watching = $derived(continueTiles(progress.items, continueWatching(Infinity), 12));
  const likes = $derived(taste(saved.items, recent.items, 3));

  $effect(() => {
    void bootDownloads();
  });
  const held = $derived(totalBytes(downloads.items));
  const readyCount = $derived(downloads.items.filter((e) => e.state === 'done').length);
  /* Counted the way the list is now drawn: a series is one thing however many
     episodes are on the device, so the card and the screen agree. */
  const readyGroups = $derived(
    groupDownloads(downloads.items.filter((e) => e.state === 'done'))
  );
  const readyLabel = $derived.by(() => {
    const shows = readyGroups.filter((g) => g.kind === 'series').length;
    const films = readyGroups.filter((g) => g.kind === 'movie').length;
    const parts: string[] = [];
    if (shows > 0) parts.push(`${shows} ${shows === 1 ? 'show' : 'shows'}`);
    if (films > 0) parts.push(`${films} ${films === 1 ? 'film' : 'films'}`);
    return parts.join(' · ');
  });
  const workingCount = $derived(
    downloads.items.filter((e) => e.state === 'downloading' || e.state === 'queued').length
  );

  /**
   * THE DOWNLOADS SCREEN IS A PANE OF THIS ROUTE, NOT A ROUTE OF ITS OWN.
   *
   * It could have been `/downloads`, and it deliberately is not: a fifth address
   * for a list that only ever arrives from one card would need its own tab rule
   * in the dock, its own entry in `nav.svelte.ts` and its own answer to "which
   * tab is lit". `#downloads` in the fragment is enough to make the card's
   * destination linkable (the title page's Downloaded state links to it) without
   * any of that, and the router ignores fragments by design.
   */
  let showDownloads = $state(false);
  $effect(() => {
    if (typeof location !== 'undefined' && location.hash === '#downloads') showDownloads = true;
  });

  /* ── the settings sheet ───────────────────────────────────────────────── */

  type Pane = 'menu' | 'app' | 'account' | 'help';
  let settingsOpen = $state(false);
  let pane = $state<Pane>('menu');

  async function openSettings(): Promise<void> {
    pane = 'menu';
    settingsOpen = true;
    await tick();
  }

  function closeSettings(): void {
    settingsOpen = false;
    picking = false;
    editing = false;
    pane = 'menu';
  }

  const paneTitle = $derived(
    pane === 'app' ? 'App settings' : pane === 'account' ? 'Account' : pane === 'help' ? 'Help' : (me?.displayName || 'You')
  );
</script>

<svelte:head><title>You — Jovii</title></svelte:head>

{#if showDownloads}
  <Downloads onback={() => (showDownloads = false)} />
{:else}
<div class="page">
  {#if view.phase === 'loading' || view.phase === 'idle'}
    <p class="line"><Spinner size={15} /> Finding your account…</p>
  {:else}
    <!--
      WHO YOU ARE, AS ONE PRESS. Mark, name, chevron. Everything the old page
      spread across four panels is behind it.
    -->
    <header class="who">
      <button class="me" type="button" aria-haspopup="dialog" aria-expanded={settingsOpen} onclick={() => void openSettings()}>
        <AvatarMark avatar={me?.avatar} color={me?.color} size={46} square />
        <span class="naming">
          <span class="name">{me?.displayName || prefs.name || 'You'}</span>
          {#if me}<span class="at">@{me.handle}</span>{/if}
        </span>
        <span class="chev"><Icon name="forward" size={18} /></span>
      </button>
    </header>

    {#if view.phase === 'unavailable'}
      <p class="say" role="status">
        <Icon name="info" size={14} />
        This build has no identity layer, so there is no handle and no friends list. Rooms still
        work and need no account at all: anyone with the link is in.
      </p>
    {:else if view.error !== null}
      <p class="say bad" role="alert"><Icon name="alert" size={14} />{view.error}</p>
    {:else if view.notice !== null}
      <p class="say good" role="status"><Icon name="check" size={14} />{view.notice}</p>
    {/if}

    {#if !view.durable && view.phase !== 'unavailable'}
      <p class="say warn" role="status">
        <Icon name="alert" size={14} />
        This server is not keeping anything: handles, friends and history are forgotten when it restarts.
      </p>
    {/if}

    <!-- DOWNLOADS, first, because it is the only thing on this page that is
         about what this device is holding rather than what the service knows. -->
    <button type="button" class="card" onclick={() => (showDownloads = true)}>
      <span class="card-icon"><Icon name="download" size={20} /></span>
      <span class="card-text">
        <span class="card-name">Downloads</span>
        <span class="card-note">
          {#if downloads.items.length === 0}
            Nothing on this device
          {:else}
            {readyLabel || `${readyCount} ready`}{#if workingCount > 0}, {workingCount} in progress{/if} · {sizeLabel(held)}
          {/if}
        </span>
      </span>
      <span class="chev"><Icon name="forward" size={18} /></span>
    </button>

    <!-- CONTINUE WATCHING sits ABOVE My List when there is anything in it: the
         thing you are in the middle of beats the thing you meant to start. -->
    {#if watching.length > 0}
      <Rail title="Continue watching" wide>
        {#each watching as tile, i (tile.item.key)}
          <ContinueCard item={tile.item} forgetKeys={tile.keys} eager={i < 2} />
        {/each}
      </Rail>
    {/if}

    <section class="shelf">
      <h2>
        My List
        {#if saved.items.length > 0}<span class="count">{saved.items.length}</span>{/if}
      </h2>
      {#if saved.items.length > 0}
        <TileGrid>
          {#each saved.items as item, i (item.id)}
            <Poster {item} eager={i < 6} />
          {/each}
        </TileGrid>
      {:else}
        <Empty
          icon="bookmark"
          title="Nothing saved yet"
          detail="Save a title that catches your eye. It’ll be here when you’re ready."
        />
      {/if}
    </section>

    <!-- SUGGESTED — `taste()`, the same function Home uses, read off this device
         from what you kept and opened. Each rail says which title it came from;
         nothing is sent anywhere to produce them. -->
    {#if likes.length > 0}
      {#each likes as like (like.kind + like.genre)}
        <LazyRail
          title={like.kind === 'series' ? `${like.genre} series` : `${like.genre} films`}
          note="because you kept {like.because}"
          kind={like.kind}
          catalog="top"
          genre={like.genre}
        />
      {/each}
    {:else}
      <LazyRail title="Popular films" kind="movie" catalog="top" />
      <LazyRail title="Popular series" kind="series" catalog="top" />
    {/if}
  {/if}
</div>
{/if}

{#if settingsOpen}
  <Sheet open title={paneTitle} onclose={closeSettings}>
    {#if pane === 'menu'}
      <div class="sheet-head">
        <button
          class="mark"
          type="button"
          aria-expanded={picking}
          aria-label="Change your mark"
          onclick={() => (picking = !picking)}
          disabled={me === null}
        >
          <AvatarMark avatar={me?.avatar} color={me?.color} size={84} square />
          <span class="markPencil"><Icon name="pencil" size={13} /></span>
        </button>

        <label class="edit">
          <span class="sr">Your name in a group</span>
          <input
            type="text"
            bind:value={draftName}
            placeholder="Add your name"
            maxlength="32"
            autocomplete="nickname"
            spellcheck="false"
            disabled={me === null}
            onblur={() => nameDirty && saveName(draftName)}
            onkeydown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
          />
        </label>
        {#if nameDirty}<p class="hint">Press Enter to save your name.</p>{/if}
      </div>

      {#if picking && me}
        <div class="picker">
          <p class="hint">Pick one that feels like you. Your friends will see it in Jovii.</p>
          <AvatarPicker
            value={me.avatar}
            color={me.color}
            options={offered}
            disabled={busyProfile}
            onpick={(a) => pickAvatar(a)}
          />
        </div>
      {/if}

      <!-- Four rows, each a full-width press with a chevron. Sleek and simple:
           the whole of the old page's four panels lives behind them. -->
      <div class="rows">
        <button type="button" class="row" onclick={() => (pane = 'app')}>
          <Icon name="sliders" size={18} />
          <span>App settings</span>
          <Icon name="forward" size={16} />
        </button>
        <button type="button" class="row" onclick={() => (pane = 'account')} disabled={me === null}>
          <Icon name="user" size={18} />
          <span>Account</span>
          <Icon name="forward" size={16} />
        </button>
        <button type="button" class="row" onclick={() => (pane = 'help')}>
          <Icon name="info" size={18} />
          <span>Help</span>
          <Icon name="forward" size={16} />
        </button>
        <a class="row" href="/welcome" onclick={closeSettings}>
          <Icon name="external" size={18} />
          <span>Sign out</span>
          <Icon name="forward" size={16} />
        </a>
      </div>
    {:else}
      <button type="button" class="back-row" onclick={() => (pane = 'menu')}>
        <Icon name="back" size={16} /> <span>Back</span>
      </button>

      {#if pane === 'app'}
        <div class="pane">
          <PlaybackPrefs />
          <p class="privacy">
            <b>Who sees what.</b> Your name, mark and handle can be found by anyone with the Jovii
            invite code so friends can add you. Private mode hides your activity. Your watch
            history stays on this device.
          </p>
          {#if me}
            <label class="privacy-toggle">
              <input
                type="checkbox"
                role="switch"
                checked={me.privateMode !== false}
                disabled={busyProfile}
                onchange={(event) => {
                  event.currentTarget.checked = me.privateMode !== false;
                  void store.saveProfile({ privateMode: me.privateMode === false });
                }}
              />
              Private mode
            </label>
            <p class="hint">
              On by default. Hide your online status and what you are watching from friends. People
              need an invitation to enter rooms you start.
            </p>
          {/if}
        </div>
      {:else if pane === 'account' && me}
        <div class="pane">
          <div class="handleRow">
            <!-- The handle IS the copy control: pressing it copies "@handle" and says
                 so with the check this page has always used. -->
            <button class="handle" type="button" onclick={copyHandle} title="Copy your handle">
              <span id="jt-handle" class="at">@{me.handle}</span>
              <span class="ok" aria-live="polite">
                {#if copied}<Icon name="check" size={13} /> Copied{/if}
              </span>
            </button>
            {#if !gate.accounts}
              <button class="pencil" type="button" aria-expanded={editing} aria-label="Change your handle" onclick={openEditor}>
                <Icon name="pencil" size={14} />
              </button>
            {/if}
          </div>

          {#if editing && !gate.accounts}
            {#if locked}
              <p class="popSay">
                <Icon name="clock" size={14} />
                <span>
                  You changed your handle recently. You can change it once a month — again
                  <b>{until(lockedFor)}</b>, {onDate(handleDeadline)}.
                </span>
              </p>
            {:else}
              <p class="popSay">
                <Icon name="info" size={14} />
                <span>You can change your handle once a month, for free.</span>
              </p>
              <form class="handleForm" onsubmit={submitHandle}>
                <div class="field">
                  <span class="fieldAt" aria-hidden="true">@</span>
                  <input
                    type="text"
                    bind:this={handleInput}
                    bind:value={draftHandle}
                    placeholder="amber-otter"
                    maxlength="24"
                    autocomplete="off"
                    autocapitalize="none"
                    spellcheck="false"
                    aria-label="New handle"
                  />
                </div>
                <div class="acts">
                  <Button onclick={() => (editing = false)}>Cancel</Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={busyHandle}
                    disabled={normaliseHandle(draftHandle) === null || normaliseHandle(draftHandle) === me.handle}
                    >Take it</Button
                  >
                </div>
              </form>
              {#if checked !== null}
                <p class="check" class:bad={!checked.available}>
                  <Icon name={checked.available ? 'check' : 'alert'} size={14} />
                  {checked.available ? `@${checked.handle} is free.` : (checked.message ?? 'That one is taken.')}
                </p>
              {:else if normaliseHandle(draftHandle) === null && draftHandle !== ''}
                <p class="check bad">
                  <Icon name="alert" size={14} /> 3–24 characters: letters, digits and single hyphens.
                </p>
              {/if}
              <p class="popNote">
                Your old handle is held for six months so nobody else can take it and be mistaken for you.
              </p>
            {/if}
          {/if}

          <dl class="counts">
            <div><dt>Watching</dt><dd>{progress.items.filter((p) => !p.finished).length}</dd></div>
            <div><dt>Saved</dt><dd>{saved.items.length}</dd></div>
            <div><dt>Friends</dt><dd>{view.friends.length}</dd></div>
          </dl>

          {#if gate.accounts}<AccountDevices />{/if}
        </div>
      {:else}
        <div class="pane">
          <p class="helpLine">
            Jovii is a private film night for a handful of friends. <a href="/welcome" onclick={closeSettings}>About Jovii</a>
            explains what it is and how a room works.
          </p>
          <InstallGuide />
        </div>
      {/if}
    {/if}
  </Sheet>
{/if}

<style>
  .page {
    padding-bottom: var(--space-6);
  }

  .line {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    margin: var(--space-6) max(var(--page-pad), var(--safe-l));
    color: var(--text-faint);
    font-size: var(--text-sm);
  }

  /* ── who you are ────────────────────────────────────────────────────── */

  .who {
    padding: var(--space-5) max(var(--page-pad), var(--safe-r)) var(--space-3)
      max(var(--page-pad), var(--safe-l));
  }
  .me {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    min-height: var(--hit);
    padding: var(--space-2);
    margin-inline: calc(var(--space-2) * -1);
    border-radius: var(--radius-lg);
    background: none;
    color: var(--text);
    text-align: start;
    touch-action: manipulation;
  }
  .me:hover {
    background: var(--surface-1);
  }
  .me:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
  .naming {
    display: grid;
    gap: 1px;
    flex: 1;
    min-width: 0;
  }
  .name {
    font-size: var(--text-xl);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .at {
    color: var(--text-faint);
    font-family: var(--mono);
    font-size: var(--text-xs);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chev {
    display: grid;
    place-content: center;
    flex: none;
    color: var(--text-faint);
  }

  /* ── the Downloads card ─────────────────────────────────────────────── */

  .card {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: calc(100% - 2 * max(var(--page-pad), var(--safe-l)));
    min-height: 64px;
    margin: var(--space-2) max(var(--page-pad), var(--safe-l)) var(--space-4);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-lg);
    border: 1px solid var(--glass-line);
    background: var(--glass-fill);
    box-shadow: var(--glass-shine);
    color: var(--text);
    text-align: start;
    touch-action: manipulation;
  }
  .card:hover {
    background: var(--glass-fill-hi);
  }
  .card:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
  .card-icon {
    display: grid;
    place-content: center;
    flex: none;
    width: 38px;
    height: 38px;
    border-radius: var(--radius);
    background: var(--surface-2);
    color: var(--text);
  }
  .card-text {
    display: grid;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }
  .card-name {
    font-size: var(--text-md);
    font-weight: var(--weight-semi);
  }
  .card-note {
    color: var(--text-faint);
    font-size: var(--text-xs);
  }

  /* ── the shelf ──────────────────────────────────────────────────────── */

  .shelf {
    padding: var(--space-4) max(var(--page-pad), var(--safe-r)) 0 max(var(--page-pad), var(--safe-l));
  }
  h2 {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
    margin-bottom: var(--space-3);
    font-size: var(--text-lg);
    font-weight: var(--weight-semi);
    letter-spacing: var(--tracking-snug);
  }
  .count {
    color: var(--text-faint);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
  }

  .say {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 0 max(var(--page-pad), var(--safe-r)) var(--space-3) max(var(--page-pad), var(--safe-l));
    color: var(--text-faint);
    font-size: var(--text-sm);
    line-height: var(--leading-snug);
    text-wrap: pretty;
  }
  .say.bad {
    color: var(--bad);
  }
  .say.good {
    color: var(--good);
  }
  .say.warn {
    color: var(--warn);
  }

  /* ── the settings sheet ─────────────────────────────────────────────── */

  .sheet-head {
    display: grid;
    justify-items: center;
    gap: var(--space-3);
    padding: var(--space-4) var(--space-4) var(--space-2);
  }
  .mark {
    position: relative;
    border-radius: var(--radius);
    background: none;
  }
  .markPencil {
    position: absolute;
    right: -4px;
    bottom: -4px;
    display: grid;
    place-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid var(--surface-1);
    background: var(--surface-3);
    color: var(--text-dim);
  }
  .mark:hover .markPencil {
    background: var(--surface-2);
    color: var(--text);
  }
  .mark:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 3px;
  }

  .edit {
    width: 100%;
    max-width: 320px;
  }
  .sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  .edit input {
    width: 100%;
    height: var(--hit);
    padding: 0 var(--space-3);
    border: 1px solid transparent;
    border-radius: var(--radius);
    background: transparent;
    color: var(--text);
    font-size: var(--text-xl);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
    text-align: center;
    outline: none;
  }
  .edit input:hover,
  .edit input:focus {
    background: var(--surface-2);
  }
  .edit input:focus {
    border-color: color-mix(in oklab, var(--focus-ring) 60%, var(--line));
  }
  .edit input::placeholder {
    color: var(--text-faint);
    font-weight: var(--weight-semi);
  }

  .picker {
    padding: 0 var(--space-4) var(--space-3);
  }

  .rows {
    display: grid;
    padding: var(--space-2) var(--space-4) var(--space-3);
  }
  .row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    min-height: 54px;
    padding: 0 var(--space-2);
    border-radius: var(--radius);
    border-top: 1px solid color-mix(in oklab, var(--line) 55%, transparent);
    background: none;
    color: var(--text);
    font-size: var(--text-md);
    text-align: start;
    touch-action: manipulation;
  }
  .row:first-child {
    border-top: 0;
  }
  .row:hover:not(:disabled) {
    background: var(--surface-2);
  }
  .row:disabled {
    opacity: 0.45;
  }
  .row:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: -2px;
  }
  /* The chevron is quieter than the label it follows. */
  .row :global(svg:last-child) {
    color: var(--text-faint);
  }

  .back-row {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    min-height: var(--hit);
    margin: 0 var(--space-4);
    padding-inline: var(--space-2);
    border-radius: var(--radius);
    color: var(--text-dim);
    font-size: var(--text-sm);
  }
  .back-row:hover {
    background: var(--surface-2);
    color: var(--text);
  }
  .pane {
    padding: 0 var(--space-4) var(--space-4);
  }

  /* ── the handle line, inside Account ────────────────────────────────── */

  .handleRow {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-top: var(--space-2);
  }
  .handle {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    min-width: 0;
    min-height: var(--hit);
    padding: 2px var(--space-2);
    margin-left: calc(var(--space-2) * -1);
    border-radius: var(--radius-sm);
    background: none;
    color: var(--text-dim);
    font-family: var(--mono);
    font-size: var(--text-sm);
    letter-spacing: 0.01em;
  }
  .handle:hover {
    background: var(--surface-2);
    color: var(--text);
  }
  .handle:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 1px;
  }
  .handle .at {
    /* Selectable, because the clipboard can be refused and reading it out loud
       is what a handle is FOR. */
    user-select: all;
  }
  .handle .ok {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: none;
    color: var(--good);
    font-family: var(--font);
    font-size: var(--text-xs);
    font-weight: var(--weight-semi);
  }
  .pencil {
    display: grid;
    place-content: center;
    width: var(--hit);
    height: var(--hit);
    flex: none;
    border-radius: var(--radius-sm);
    background: none;
    color: var(--text-faint);
  }
  .pencil:hover {
    background: var(--surface-2);
    color: var(--text);
  }
  .pencil:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 1px;
  }

  .popSay {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: var(--space-2);
    color: var(--text-dim);
    font-size: var(--text-sm);
    line-height: var(--leading-snug);
    text-wrap: pretty;
  }
  .popSay b {
    color: var(--text);
  }
  .popNote {
    margin-top: var(--space-2);
    color: var(--text-faint);
    font-size: var(--text-xs);
    line-height: var(--leading-snug);
    text-wrap: pretty;
  }
  .handleForm {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin-top: var(--space-3);
  }
  .field {
    display: flex;
    align-items: center;
    flex: 1 1 180px;
    min-width: 0;
    height: var(--hit);
    padding-left: var(--space-3);
    border-radius: var(--radius);
    border: 1px solid var(--line);
    background: var(--surface-2);
  }
  .field:focus-within {
    border-color: color-mix(in oklab, var(--focus-ring) 60%, var(--line));
  }
  .fieldAt {
    color: var(--text-faint);
    font-family: var(--mono);
  }
  .field input {
    flex: 1;
    min-width: 0;
    height: 100%;
    padding-inline: 4px var(--space-3);
    background: transparent;
    border: 0;
    color: var(--text);
    font-family: var(--mono);
    /* 16px so iOS does not zoom the page on focus. */
    font-size: var(--text-md);
    outline: none;
  }
  .acts {
    display: flex;
    gap: var(--space-2);
  }

  .check {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: var(--space-2);
    color: var(--good);
    font-size: var(--text-xs);
    line-height: var(--leading-snug);
  }
  .check.bad {
    color: var(--bad);
  }

  .hint {
    color: var(--text-faint);
    font-size: var(--text-xs);
    line-height: var(--leading);
    text-wrap: pretty;
  }

  .privacy-toggle {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-height: var(--hit);
    margin-top: var(--space-3);
    cursor: pointer;
  }
  .privacy-toggle input {
    width: 20px;
    height: 20px;
    accent-color: var(--accent);
  }

  .counts {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-2);
    margin: var(--space-4) 0;
  }
  .counts div {
    display: grid;
    gap: 2px;
    padding: var(--space-3);
    border-radius: var(--radius-lg);
    border: 1px solid var(--glass-line);
    background: var(--glass-fill);
    box-shadow: var(--glass-shine);
    text-align: center;
  }
  .counts dt {
    color: var(--text-faint);
    font-size: var(--text-2xs);
    font-weight: var(--weight-semi);
    letter-spacing: var(--tracking-wide);
    text-transform: uppercase;
  }
  .counts dd {
    margin: 0;
    font-size: var(--text-2xl);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
  }

  .privacy,
  .helpLine {
    margin-top: var(--space-4);
    color: var(--text-faint);
    font-size: var(--text-xs);
    line-height: var(--leading);
    text-wrap: pretty;
  }
  .privacy b {
    color: var(--text-dim);
  }
  .helpLine a {
    color: var(--text);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
</style>